/*
 * Copyright (c) 2024 Roboto Technologies, Inc.
 */

import { Alert, Box, List } from "@mui/material";
import * as React from "react";

import { AuthZCategoryListItem } from "@/shared/components/AuthZCategoryListItem";
import { useAPICall } from "@/shared/services/apiHooks";
import { AuthZTuple, GetAccessResponse } from "@/types";

import { collectionsAccessEndpoint } from "../api";

interface CollectionPermissionDetailsProps {
  collectionId: string;
}

export const CollectionPermissionDetails: React.FC<
  CollectionPermissionDetailsProps
> = ({ collectionId }) => {
  const { data, error, loading, refetch } = useAPICall<GetAccessResponse>({
    endpoint: collectionsAccessEndpoint,
    method: "GET",
    pathParams: { collectionId: collectionId },
  });

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const relationsToTuples: Map<string, AuthZTuple[]> = new Map();

  if (data) {
    data.data.relations.forEach((relation) => {
      const tuples = relationsToTuples.get(relation.relation) || [];
      tuples.push(relation);
      relationsToTuples.set(relation.relation, tuples);
    });
  }

  const editAccessEndpointPathParams = { collectionId: collectionId };

  return (
    <Box>
      <List sx={{ width: "100%" }}>
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Owner"}
          editAccessEndpoint={collectionsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"owner"}
          relations={relationsToTuples.get("owner") || []}
          resourceId={collectionId}
          resourceType={"collection"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Group(s)"}
          editAccessEndpoint={collectionsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"associated_group"}
          relations={relationsToTuples.get("associated_group") || []}
          resourceId={collectionId}
          resourceType={"collection"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Viewer"}
          editAccessEndpoint={collectionsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"viewer"}
          relations={relationsToTuples.get("viewer") || []}
          resourceId={collectionId}
          resourceType={"collection"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Editor"}
          editAccessEndpoint={collectionsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"editor"}
          relations={relationsToTuples.get("editor") || []}
          resourceId={collectionId}
          resourceType={"collection"}
          loading={loading}
        />
      </List>
    </Box>
  );
};
